import * as React from "react";
import { createPortal } from "react-dom";
import { useIsLoggedIn } from "@/shared/queries/loggedInStatus.ts";
import { useClientOutdated } from "@/shared/queries/minimumCompatibleVersion.ts";
import {
  dismissSystemError,
  useSystemErrorVisible,
} from "@/shared/queries/networkState.ts";
import { getCvPartner } from "@/shared/util/getCvPartner.ts";
import { I18n } from "@/shared/util/translations.ts";

const ErrorMessage: React.FC = () => {
  const visible = useSystemErrorVisible();

  if (!visible) {
    return null;
  }

  return (
    <div className="system_errormessage error">
      <span>{I18n.t("error_processing_data")}</span>{" "}
      <div className="close" onClick={dismissSystemError}>
        {I18n.t("error_labels.close")}
      </div>{" "}
      <span>{I18n.t("error_labels.or")}</span>{" "}
      <div className="reload" onClick={() => window.location.reload()}>
        {I18n.t("error_labels.reload")}
      </div>
    </div>
  );
};

const UpdateMessage: React.FC = () => {
  const outdated = useClientOutdated();

  if (!outdated) {
    return null;
  }

  return (
    <div className="system_updatemessage error">
      <span>{I18n.t("error_new_functionality")}</span>{" "}
      <div className="reload" onClick={() => window.location.reload()}>
        {I18n.t("error_labels.reload")}
      </div>
    </div>
  );
};

function redirectToLoginTimeout() {
  window.location.replace("/login/timeout");
}

export const LoggedOutRedirector: React.FC<{
  redirectToTimeout?: () => void;
}> = ({ redirectToTimeout = redirectToLoginTimeout }) => {
  const isLoggedIn = useIsLoggedIn(true);
  const loggedOut = isLoggedIn === false;

  React.useEffect(() => {
    if (loggedOut) {
      redirectToTimeout();
    }
  }, [loggedOut, redirectToTimeout]);

  return null;
};

const CONTAINER_ID = "system-messages-portal";

function getContainer() {
  const existing = document.getElementById(CONTAINER_ID);
  if (existing) {
    return existing;
  }

  const element = document.createElement("div");
  element.id = CONTAINER_ID;
  document.body.appendChild(element);

  return element;
}

const container = getContainer();

export const SystemMessages: React.FC = () => {
  const checksEnabled = !getCvPartner().disableTimeoutAndCompatibilityChecks;

  return createPortal(
    <>
      <ErrorMessage />
      {checksEnabled && (
        <>
          <UpdateMessage />
          <LoggedOutRedirector />
        </>
      )}
    </>,
    container,
  );
};
